import { Component, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { SearchSelectComponent } from '../../../../components/search-select/search-select.component';
import { OrganizationService } from '../../organization.service';
import { NzMessageService, NzMessageModule } from 'ng-zorro-antd/message';
import { ErrorResponse } from '../../../../core/models/error.models';
import { CommonService } from '../../../../shared/services/common.service';
import { BehaviorSubject, finalize, Subject, takeUntil } from 'rxjs';
import { AsyncPipe, CommonModule } from '@angular/common';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { DrawerService } from '../../../../core/services/drawer.service';

@Component({
  selector: 'app-import-one-roster-job',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NzDropDownModule,
    NzSelectModule,
    NzUploadModule,
    NzButtonModule,
    NzMessageModule,
    AsyncPipe,
    NzAlertModule,
    CommonModule,
    SearchSelectComponent,
  ],
  templateUrl: './import-one-roster-job.component.html',
  styleUrl: './import-one-roster-job.component.scss',
})
export class ImportOneRosterJobComponent implements OnDestroy {
  organizationForm!: FormGroup;
  private readonly isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();
  private readonly destroy$ = new Subject<void>();

  private readonly sftpStatusSubject = new BehaviorSubject<{
    isSftpEnabled: boolean;
    isChecking: boolean;
    errorMessage: string | null;
  }>({
    isSftpEnabled: false,
    isChecking: false,
    errorMessage: null,
  });

  sftpStatus$ = this.sftpStatusSubject.asObservable();

  constructor(
    private readonly fb: FormBuilder,
    private readonly organizationService: OrganizationService,
    private readonly message: NzMessageService,
    private readonly commonService: CommonService,
    private readonly drawerService: DrawerService
  ) {
    this.organizationForm = this.fb.group({
      orgparentorg: [null, [Validators.required]],
      isSftpEnabled: [null, [Validators.required]],
    });

    this.organizationForm
      .get('orgparentorg')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.organizationForm.get('isSftpEnabled')?.setValue(true);
          this.checkSftpStatus(value);
        } else {
          this.organizationForm.get('isSftpEnabled')?.setValue(null);
          this.resetSftpStatus();
        }
      });
  }

  get isImportButtonDisabled(): boolean {
    const sftpStatus = this.sftpStatusSubject.getValue();
    return (
      !this.organizationForm.get('orgparentorg')?.value ||
      this.isLoadingSubject.getValue() ||
      sftpStatus.isChecking ||
      !sftpStatus.isSftpEnabled
    );
  }

  resetSftpStatus(): void {
    this.sftpStatusSubject.next({
      isSftpEnabled: false,
      isChecking: false,
      errorMessage: null,
    });
  }

  checkSftpStatus(organizationId: number): void {
    this.sftpStatusSubject.next({
      ...this.sftpStatusSubject.getValue(),
      isChecking: true,
      errorMessage: null,
    });

    this.organizationService
      .getSftpStatus(organizationId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.sftpStatusSubject.next({
            ...this.sftpStatusSubject.getValue(),
            isChecking: false,
          });
        })
      )
      .subscribe({
        next: response => {
          if (response.result) {
            this.sftpStatusSubject.next({
              isSftpEnabled: response.result.isSftpEnabled,
              isChecking: false,
              errorMessage: null,
            });
          } else {
            this.sftpStatusSubject.next({
              isSftpEnabled: false,
              isChecking: false,
              errorMessage: 'SFTP is not configured for this organization.',
            });
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          let errorMsg = 'Error checking SFTP status.';

          if (errors && errors.length > 0) {
            errorMsg = errors
              .map(err => err.description || err.detailedMessage)
              .join(', ');
          } else if (message) {
            errorMsg = message;
          }

          this.sftpStatusSubject.next({
            isSftpEnabled: false,
            isChecking: false,
            errorMessage: errorMsg,
          });
        },
      });
  }

  importFile(): void {
    const selectedOrgId = this.organizationForm.get('orgparentorg')?.value;

    if (!selectedOrgId) {
      this.message.error('Please select an organization first');
      return;
    }

    this.isLoadingSubject.next(true);

    this.organizationService
      .triggerSftpUpload(selectedOrgId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingSubject.next(false);
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'The SFTP task has been initiated. You will receive an email once the task is complete.'
          );
          this.closeDrawer();
        },
        error: ({ message }: ErrorResponse) => {
          this.commonService.notify(
            'error',
            'OneRoster file not found. Please ensure it is uploaded to the SFTP service.',
            message
          );
        },
      });
  }

  closeDrawer(): void {
    this.resetSftpStatus();
    this.organizationForm.reset();
    this.drawerService.closeAllDrawers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
